import { QueryClient } from '@tanstack/react-query';
import { setMustChangePasswordHandler, setUnauthorizedHandler } from '@/api/client';
import { isApiError } from '@/api/errors';
import { SESSION_QUERY_KEY, setSession } from '@/auth/session';
import type { AdminSession } from '@/api/types';

const MAX_RETRIES = 2;

export function createQueryClient(): QueryClient {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        refetchOnWindowFocus: false,
        // Client errors will not change on retry; only transient failures are retried.
        retry: (failureCount, error) =>
          failureCount < MAX_RETRIES &&
          (!isApiError(error) || error.isNetworkError || error.status >= 500),
      },
      mutations: { retry: false },
    },
  });

  // A 401 on any call means the session is gone: forget it, and the route guard sends the
  // user to the sign-in page with the current location to come back to.
  setUnauthorizedHandler(() => setSession(queryClient, null));

  // 403 must_change_password: mark the cached session so the route guard shows the
  // change-password screen instead of "not permitted".
  setMustChangePasswordHandler(() =>
    queryClient.setQueryData<AdminSession | null>(SESSION_QUERY_KEY, (current) =>
      current ? { ...current, must_change_password: true } : current,
    ),
  );

  return queryClient;
}
